/**
 * Add Location Inline Component
 * Provides inline input for adding new locations quickly
 * Follows Single Responsibility Principle
 */

import React, { useState, useRef, useEffect } from 'react';
import { Plus, Check, X } from 'lucide-react';
import { serviceLogger } from '../../utils/serviceLogger';

export const AddLocationInline = ({ onAdd, isAdding }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [locationName, setLocationName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleStartAdding = () => {
    setIsEditing(true);
    setLocationName('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setLocationName('');
  };

  const handleConfirm = async () => {
    const trimmedName = locationName.trim();
    if (!trimmedName) {
      handleCancel();
      return;
    }

    try {
      setIsSubmitting(true);
      await onAdd(trimmedName);
      setIsEditing(false);
      setLocationName('');
    } catch (error) {
      serviceLogger.error('Error adding location:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleConfirm();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="bg-white border-2 border-[#61b8e7] rounded-[10px] px-4 py-3 flex items-center gap-3">
        <input
          ref={inputRef}
          type="text"
          value={locationName}
          onChange={(e) => setLocationName(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter area name..."
          disabled={isSubmitting}
          className="flex-1 font-['Roboto:Regular',sans-serif] font-normal text-[15px] text-black bg-transparent border-none outline-none placeholder:text-[#b9b9b9]"
          style={{ fontVariationSettings: "'wdth' 100" }}
        />
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting || !locationName.trim()}
            className="hover:bg-green-50 disabled:opacity-30 rounded-md p-1.5 transition-colors"
            aria-label="Confirm"
          >
            <Check className="w-4 h-4 text-green-600" strokeWidth={2.5} />
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSubmitting}
            className="hover:bg-red-50 disabled:opacity-30 rounded-md p-1.5 transition-colors"
            aria-label="Cancel"
          >
            <X className="w-4 h-4 text-red-600" strokeWidth={2.5} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={handleStartAdding}
      disabled={isAdding}
      className="group bg-gradient-to-br from-[#f8fbfd] to-white border-2 border-[#61b8e7]/30 rounded-[12px] h-[90px] flex items-center justify-center gap-3 hover:border-[#61b8e7] hover:shadow-md hover:shadow-[#61b8e7]/10 transition-all w-full disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <div className="bg-[#61b8e7] rounded-[6px] w-[32px] h-[32px] flex items-center justify-center group-hover:scale-110 transition-transform">
        <Plus className="w-[18px] h-[18px] text-white" strokeWidth={3} />
      </div>
      <span
        className="font-['Roboto:Medium',sans-serif] font-medium text-[15px] text-[#61b8e7] group-hover:text-[#4da3d1]"
        style={{ fontVariationSettings: "'wdth' 100" }}
      >
        Add New Area
      </span>
    </button>
  );
};
